import { Translation } from "../../hooks/useTranslations";
import { Entity, Version } from "../../types";
import { AdminCarDriver, CarDriver, CarDriverTypeOfUse } from "../carDrivers/types";
import { CarLeasing, UpdateCarLeasing } from "../carLeasings/types";
import { CarPlate } from "../carPlates/types";
import { CarSubsidiary } from "../carSubsidiaries/types";
import { CarTaskLog } from "../carTaskLogs/types";
import { DueDate } from "../dueDates/types";
import { HighwayPass } from "../highwayPasses/types";
import { Manufacturer } from "../manufacturers/types";
import { Mileage } from "../mileages/types";
import { AssignmentType, OtherCarAssignment } from "../otherCarAssignment/types";
import { Language } from "../subsidiaries/types";
import { RentalCar } from "../rentalCars/types";

export type CarState = "active" | "sold" | "returned";

export type Assignment = {
  carDriver?: AdminCarDriver;
  otherCarAssignment?: OtherCarAssignment;
};

export type Car = Entity & {
  vehicleIdentificationNumber: string;
  notes?: string;
  manufacturer: Manufacturer;
  model: string;
  hp?: number;
  displacement?: string;
  co2Emissions?: string;
  fuelType: string;
  category?: CarCategoryType;
  isPrivateCar: boolean;
  status: CarState;
  carSubsidiary?: CarSubsidiary;
  leasing?: CarLeasing;
  currentPlateNumber?: string;
  lastPlateNumber?: string;
  lastMileage?: number;
  purchaseType?: string;
  registrationDate?: string;
  currentAssignment: Assignment;
  purchaseDate?: string;
  soldDate?: string;
};

export type CarNoCarDepency = Omit<Car, "carSubsidiary" | "currentAssignment" | "leasing"> & {
  carSubsidiary?: {
    id: string;
    subsidiary: {
      id: string;
      name: string;
      semiramisOrganizationCode?: string;
    };
    startDate: string;
    endDate?: string;
  };
  currentAssignment: {
    carDriver?: {
      id: string;
      driver: {
        id: string;
        fullName: string;
        currentCostCenter?: string;
      };
      startDate: string;
      endDate?: string;
    };
    otherCarAssignment: {
      id: string;
      startDate: string;
      endDate?: string;
      type: AssignmentType;
      costCenter: string;
    };
  };
};

export type CarVersion = Version & {
  vehicleIdentificationNumber: string;
  notes?: string;
  manufacturer?: Manufacturer;
  model: string;
  hp: number;
  displacement?: string;
  fuelType: string;
  category: CarCategoryType;
  isPrivateCar: boolean;
  status: CarState;
  leasing?: CarLeasing;
  currentPlateNumber?: string;
  lastPlateNumber?: string;
  currentDriverFullName?: string;
  purchaseType?: string;
  registrationDate?: string;
  purchaseDate?: string;
  soldDate?: string;
};

export type UpdateCar = {
  vehicleIdentificationNumber: string;
  notes?: string;
  manufacturerId: string;
  model: string;
  hp: number;
  displacement?: string;
  co2Emissions?: string;
  fuelType: string;
  category?: CarCategoryType;
  isPrivateCar: boolean;
  status: CarState;
  leasing?: UpdateCarLeasing;
  plateNumber?: string;
  registrationDate?: string;
  currentVersionNumber?: number;
  purchaseDate?: string;
  soldDate?: string;
};

export type InsertCar = UpdateCar & {
  driverId?: string;
  typeOfUse?: string;
  carPurchaseId?: string | null;
  subsidiaryId: string;
};

export type CarCategory = {
  value: CarCategoryType;
  label: string;
};

export type CarCategoryType = "pkw" | "lkw" | "pkw-superior" | "pkw-up";

export const getCategories = (i18n: Translation): CarCategory[] => [
  { value: "pkw", label: i18n.common.pkw },
  { value: "lkw", label: i18n.common.lkw },
  { value: "pkw-superior", label: i18n.common.pkwSuperior },
  { value: "pkw-up", label: i18n.common.pkwUp },
];

export type Status = {
  value: CarState;
  label: string;
};

export const getStatus = (i18n: Translation): Status[] => [
  { value: "active", label: i18n.common.active },
  { value: "sold", label: i18n.common.sold },
  { value: "returned", label: i18n.common.returned },
];

export type FuelType = {
  value: string;
  label: string;
};

export const getFuelTypes = (i18n: Translation): FuelType[] => [
  { value: "diesel", label: i18n.common.diesel },
  { value: "gasoline", label: i18n.common.gasoline },
  { value: "electric", label: i18n.common.electric },
  { value: "gpl", label: i18n.common.gpl },
  { value: "hybrid", label: i18n.common.hybrid },
  { value: "plug-in-hybrid", label: i18n.common.plugInHybrid },
];

export type TypeOfUse = {
  value: CarDriverTypeOfUse;
  label: string;
};

export const getTypesOfUse = (i18n: Translation): TypeOfUse[] => [
  { value: "private", label: i18n.common.private },
  { value: "business", label: i18n.common.business },
];

export const getDelegationLanguages = (t: Translation): Language[] => [
  { value: "it", label: t.common.italian },
  { value: "en", label: t.common.english },
  { value: "de", label: t.common.german },
  { value: "es", label: t.common.spanish },
  { value: "fr", label: t.common.french },
  { value: "pt", label: t.common.portuguese },
];

export type AdminCarOverview = {
  car: Car;
  currentAssignment: CarCurrentAssignment;
  carTasks: CarTaskLog[];
  highwayPass?: HighwayPass;
  dueDates: DueDate[];
  lastMileage?: Mileage;
  leasing?: CarLeasing;
  carPlate?: CarPlate;
  rentalCar?: RentalCar;
};

export type MyCar = {
  id: string;
  vehicleIdentificationNumber: string;
  manufacturer: string;
  model: string;
  hp?: number;
  displacement?: number;
  fuelType?: string;
  subsidiary: string;
  plateNumber?: string;
  driver?: CarDriver;
  registrationDate?: string;
  highWayPass?: string;
};

export type CarCurrentAssignment = {
  carDriver?: AdminCarDriver;
  otherCarAssignment?: OtherCarAssignment;
};

export type CreateCarDelegationBody = {
  driversName: string[];
  typeOfUse: string;
  language: string;
};

export type CarStats = {
  totalActive: number;
  totalAssigned: number;
  totalNotInUse: number;
  totalCarPool: number;
  avgAgeYears?: number;
};

export const getCarStateColor = (state?: CarState): "yellow" | "red" | "green" => {
  switch (state) {
    case "sold":
      return "red";
    case "active":
      return "green";
    default:
      return "yellow";
  }
};

export type CarNotification = {
  dueDateExpiringSoon: number;
  dueDateExpired: number;
  taskToDo: number;
};
